import {
  Row,
  Typography,
  Dropdown,
  MenuProps,
  Button,
  Modal,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { storageApi } from '@/api';
import DuplicateIcon from '@/assets/icons/icon-folder.svg?react';
import InfoIcon from '@/assets/icons/icon-info.svg?react';
import DeactivateIcon from '@/assets/icons/icon-power.svg?react';
import VerticalEllipsisIcon from '@/assets/icons/icon-three-dots-vertical.svg?react';
import ViewIcon from '@/assets/icons/icon-view-second.svg?react';
import RNotificationCard from '@/components/RNotificationCard';
import RTag from '@/components/RTag';
import CustomTooltip from '@/components/RTooltip';
import { TourSteps } from '@/constants';
import useProductTourStore from '@/store/useProductTourStore';
import { DrawerType } from '@/types/global';
import { StorageSystem } from '@/types/storageSystem';
import { capitalizeFirstLetter, handleError } from '@/utils';

import useProductTour from './useProductTour';

const useStorageSystemsTableColumns = (
  mutate: () => void,
  updateDrawer: (
    type: DrawerType<StorageSystem>['type'],
    data: DrawerType<StorageSystem>['data']
  ) => void
) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const { currentStep, isTourActive } = useProductTourStore();
  const dropdownWrapperRef = useProductTour('viewDeactivateStorageSystem');

  const handleActivate = async (record: StorageSystem) => {
    return modal.confirm({
      closeIcon: false,
      footer: null,
      width: 475,
      modalRender: () => (
        <RNotificationCard
          title={t('storageSystem.notifications.activate.title', {
            name: capitalizeFirstLetter(record.name),
          })}
          message={t('storageSystem.notifications.activate.message')}
          type="warning"
          confirmText={t('common.yes')}
          cancelText={t('common.cancel')}
          open={!record.isActive}
          onCancel={() => Modal.destroyAll()}
          onConfirm={async () => {
            try {
              await storageApi.activateStorageSystem(record.id);
              mutate();
              Modal.destroyAll();
              notification.success({
                message: t('storageSystem.notifications.activated', {
                  name: capitalizeFirstLetter(record.name),
                }),
              });
            } catch (error) {
              Modal.destroyAll();
              handleError(error);
            }
          }}
        />
      ),
    });
  };

  const handleDeactivate = async (record: StorageSystem) => {
    return modal.confirm({
      // TODO CHANGE THIS OPEN LOGIC
      closeIcon: false,
      footer: null,
      width: 475,
      modalRender: () => (
        <RNotificationCard
          title={t('storageSystem.notifications.deactivate.title', {
            name: capitalizeFirstLetter(record.name),
          })}
          message={t('storageSystem.notifications.deactivate.message')}
          type="warning"
          confirmText={t('common.yes')}
          cancelText={t('common.cancel')}
          open={record.isActive}
          onCancel={() => Modal.destroyAll()}
          onConfirm={async () => {
            try {
              await storageApi.deactiveStorageSystem(record.id);
              mutate();
              Modal.destroyAll();
              notification.success({
                message: t('storageSystem.notifications.deactivated', {
                  name: capitalizeFirstLetter(record.name),
                }),
              });
            } catch (error) {
              Modal.destroyAll();
              handleError(error);
            }
          }}
        />
      ),
    });
  };

  const handleView = (record: StorageSystem) => {
    updateDrawer('view', record);
  };

  const handleDuplicate = (record: StorageSystem) => {
    updateDrawer('duplicate', record);
  };

  const generateItems = (record: StorageSystem) => {
    const items: MenuProps['items'] = [
      {
        label: t('storageSystem.table.view'),
        key: 'view-scenario',
        icon: <ViewIcon style={{ width: '16px', height: '16px' }} />,
        disabled: isTourActive,
      },
      {
        label: t('storageSystem.table.duplicate'),
        key: 'duplicate-scenario',
        icon: <DuplicateIcon style={{ width: '16px', height: '16px' }} />,
        disabled: isTourActive,
      },
    ];

    if (record.isActive) {
      items.push({
        label: t('storageSystem.table.deactivate'),
        key: 'deactivate-scenario',
        icon: <DeactivateIcon style={{ width: '16px', height: '16px' }} />,
        disabled: isTourActive,
      });
    } else {
      items.push({
        label: t('storageSystem.table.activate'),
        key: 'activate-scenario',
        icon: <DeactivateIcon style={{ width: '16px', height: '16px' }} />,
        disabled: isTourActive,
      });
    }

    return items;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMenuClick = (info: any, record: StorageSystem) => {
    switch (info.key) {
      case 'view-scenario':
        handleView(record);
        break;
      case 'duplicate-scenario':
        handleDuplicate(record);
        break;
      case 'deactivate-scenario':
        handleDeactivate(record);
        break;
      case 'activate-scenario':
        handleActivate(record);
        break;
      default:
        break;
    }
  };

  function getRTEColor(rte: number) {
    if (rte < 45) return theme.colors.red;
    if (rte < 70) return theme.colors.orangePrimary;
    return theme.colors.greenPrimary;
  }

  const columns: ColumnsType<StorageSystem> = [
    {
      title: t('storageSystem.table.name'),
      dataIndex: 'name',
      key: 'name',
      width: '%15',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name) => (
        <Row align="middle">
          <Typography.Paragraph
            style={{ maxWidth: '200px' }}
            className="text"
            ellipsis={{
              rows: 1,
              tooltip: name,
              expandable: false,
            }}
          >
            {name}
          </Typography.Paragraph>
        </Row>
      ),
    },
    {
      title: t('storageSystem.table.description'),
      dataIndex: 'description',
      key: 'description',
      width: '%25',
      render: (description) => (
        <Row align="middle">
          <Typography.Paragraph
            className="text"
            style={{ maxWidth: '200px' }}
            ellipsis={{
              rows: 1,
              tooltip: description,
              expandable: false,
            }}
          >
            {description}
          </Typography.Paragraph>
        </Row>
      ),
    },
    {
      title: t('storageSystem.table.roundTripEfficiency'),
      dataIndex: 'netOverallRoundTripEfficiency',
      key: 'netOverallRoundTripEfficiency',
      sorter: (a, b) =>
        a.netOverallRoundTripEfficiency - b.netOverallRoundTripEfficiency,
      render: (text) => (
        <Row align="middle" justify="end">
          {text ? (
            <Typography.Text style={{ color: getRTEColor(text) }}>
              <b>{text}%</b>
            </Typography.Text>
          ) : (
            <Typography.Text>
              <b>-</b>
            </Typography.Text>
          )}
        </Row>
      ),
    },
    {
      title: t('storageSystem.table.ratedEnergy'),
      dataIndex: 'ratedEnergyMwh',
      key: 'ratedEnergyMwh',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => (
        <Row align="middle" justify="end">
          <Typography.Text>{text}</Typography.Text>
        </Row>
      ),
    },
    {
      title: t('storageSystem.table.ratedPower'),
      dataIndex: 'ratedPowerMw',
      key: 'ratedPowerMw',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => (
        <Row align="middle" justify="end">
          <Typography.Text>{text}</Typography.Text>
        </Row>
      ),
    },
    {
      title: t('storageSystem.table.creationDate'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (data) => (
        <Row align="middle">
          <Typography.Paragraph
            style={{ maxWidth: '200px' }}
            className="text"
            ellipsis={{
              rows: 1,
              tooltip: data,
              expandable: false,
            }}
          >
            {dayjs(data).format('DD.MM.YYYY HH:MM')}
          </Typography.Paragraph>
        </Row>
      ),
    },
    {
      title: (
        <CustomTooltip
          className="cursor-pointer"
          title={t('storageSystem.table.tooltip.statusTitle')}
          description={t('storageSystem.table.tooltip.statusDesc')}
          trigger="click"
        >
          <Row align="middle" justify="start" className="w-100">
            <Typography.Text className="pr-8">
              {t('storageSystem.table.status')}
            </Typography.Text>
            <InfoIcon fill={theme.colors.dark} />
          </Row>
        </CustomTooltip>
      ),
      sorter: (a, b) => String(a.isActive).localeCompare(String(b.isActive)),
      dataIndex: 'isActive',
      key: 'isActive ',
      align: 'left',
      width: '%10',
      render: (dataIndex) =>
        dataIndex ? <RTag type="active" /> : <RTag type="inactive" />,
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions ',
      width: '60px',
      render: (_, record: StorageSystem, index: number) => (
        <>
          {contextHolder}
          <div
            ref={index === 0 ? dropdownWrapperRef : undefined}
            style={{
              width: '32px',
              height: '32px',
            }}
          >
            <Dropdown
              trigger={['click']}
              menu={{
                items: generateItems(record),
                onClick: (info) => handleMenuClick(info, record),
              }}
              open={
                index === 0 &&
                isTourActive &&
                currentStep === TourSteps.VIEW_STORAGE_SYSTEMS
                  ? true
                  : undefined
              } // Menu will automatically open when tour is active
              placement="bottomRight"
            >
              <Button
                className="default-icon-button"
                type="default"
                icon={<VerticalEllipsisIcon fill={theme.colors.bluePrimary} />}
              ></Button>
            </Dropdown>
          </div>
        </>
      ),
    },
  ];

  return columns;
};

export default useStorageSystemsTableColumns;
